import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useContext, useEffect, useMemo } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Search,
  RotateCcw,
  Plus,
  ArrowLeft,
  Save,
  Building2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const SupplierMappingAdd = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const [supplierCode, setSupplierCode] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const fetchSuppliers = async (
    pageNo = page,
    supplierCodeValue = supplierCode,
    supplierNameValue = supplierName
  ) => {
    if (!branchId) {
      toast.error("Branch ID not found in localStorage");
      setSuppliers([]);
      setTotalPages(1);
      setTotalRecords(0);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        "https://nissi-india.com/api/supplierMainMasterForBranchAdd",
        {
          params: {
            branchid: branchId,
            suppliercode: supplierCodeValue || "",
            suppliername: supplierNameValue || "",
            page: pageNo,
            limit,
          },
          headers: commonHeaders,
        }
      );

      const data = res.data;

      if (Array.isArray(data.supplierList)) {
        setSuppliers(data.supplierList);
        setTotalRecords(data.totalRecords || 0);
        setTotalPages(data.totalPages || 1);
      } else {
        setSuppliers([]);
        setTotalPages(1);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Failed to fetch suppliers from branch add API:", error);
      toast.error("Failed to fetch suppliers from branch add API");
      setSuppliers([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers(page);
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    fetchSuppliers(1);
  };

  const handleReset = () => {
    setSupplierCode("");
    setSupplierName("");
    setPage(1);
    fetchSuppliers(1, "", "");
  };

  const handleSaveMapping = async () => {
    if (selectedSuppliers.length === 0) {
      toast.warning("No suppliers selected");
      return;
    }

    if (!branchId || !branch) {
      toast.error("Branch details not found in localStorage");
      return;
    }

    try {
      const payload = {
        supplierMainIds: selectedSuppliers,
        branchid: branchId,
        branchname: branch,
      };

      const res = await axios.post(
        "https://nissi-india.com/api/insertSupplierWithBranch",
        payload,
        {
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        toast.success(res.data?.message || "Suppliers mapped successfully!");
        setSelectedSuppliers([]);
        fetchSuppliers(page);
      } else {
        toast.error(res.data?.message || res.data?.error || "Mapping failed");
      }
    } catch (error) {
      console.error("Error mapping suppliers:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Error mapping suppliers"
      );
    }
  };

  const handleCheckboxChange = (supplierMainId) => {
    setSelectedSuppliers((prev) =>
      prev.includes(supplierMainId)
        ? prev.filter((id) => id !== supplierMainId)
        : [...prev, supplierMainId]
    );
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Supplier Mapping Add</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header + Filters Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Supplier Mapping Add</h1>
                <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search suppliers and map them to the current branch
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-200 hover:bg-slate-700/50"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <ArrowLeft size={16} />
                Back
              </button>

              <button
                onClick={() => navigate("/inventory/Supplier-MainMas")}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <Plus size={16} />
                Add New
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={labelClass}>Supplier Code</label>
              <input
                type="text"
                value={supplierCode}
                onChange={(e) => setSupplierCode(e.target.value)}
                placeholder="Enter supplier code"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Supplier Name</label>
              <input
                type="text"
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder="Enter supplier name"
                className={inputClass}
              />
            </div>

            <div className="flex items-end gap-2 lg:col-span-2">
              <button
                onClick={handleSearch}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <Search size={16} />
                Search
              </button>
              <button
                onClick={handleReset}
                title="Clear filters"
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-200 hover:bg-slate-700/50"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Available Suppliers</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {totalRecords} suppliers
                </span>
              </div>

              <button
                onClick={handleSaveMapping}
                disabled={selectedSuppliers.length === 0}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Save size={16} />
                Save ({selectedSuppliers.length})
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Supplier Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Supplier Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">State</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Zipcode</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Mobile No</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Select</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton height={20} baseColor={isDark ? "#374151" : "#f3f4f6"} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : suppliers.length > 0 ? (
                  suppliers.map((s, idx) => {
                    const supplierMainId =
                      s.fld_suppliermainid || s.suppliermainid || s.supplierMainId;

                    return (
                      <tr
                        key={supplierMainId || idx}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium">
                          {(page - 1) * limit + idx + 1}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {s.fld_suppliercode || s.suppliercode || ""}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {s.fld_suppliername || s.suppliername || ""}
                        </td>
                        <td className="px-4 py-3">
                          {s.fld_statename || s.state || ""}
                        </td>
                        <td className="px-4 py-3">
                          {s.fld_ZipCode || s.zipcode || ""}
                        </td>
                        <td className="px-4 py-3">
                          {s.fld_Mobile || s.mobile || ""}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedSuppliers.includes(supplierMainId)}
                            onChange={() => handleCheckboxChange(supplierMainId)}
                            disabled={!supplierMainId}
                            className="w-4 h-4 rounded"
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Showing{" "}
                <span className="font-semibold">
                  {totalRecords === 0 ? 0 : (page - 1) * limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold">
                  {Math.min(page * limit, totalRecords)}
                </span>{" "}
                of <span className="font-semibold">{totalRecords}</span> results
              </div>

              {loading ? (
                <div className="flex space-x-2">
                  <Skeleton width={80} height={36} borderRadius={10} />
                  <Skeleton width={36} height={36} borderRadius={10} />
                  <Skeleton width={36} height={36} borderRadius={10} />
                  <Skeleton width={80} height={36} borderRadius={10} />
                </div>
              ) : (
                <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    <ChevronLeft size={16} />
                    Prev
                  </button>

                  {[...Array(Math.min(totalPages, 5))].map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPage(idx + 1)}
                      className={`px-4 py-2 text-sm font-medium transition ${
                        page === idx + 1
                          ? `bg-${baseColor}-${baseShade} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || totalPages === 0}
                    className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                      page === totalPages || totalPages === 0
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default SupplierMappingAdd;